import { useMemo } from 'react';
import { useQuery, NetworkStatus } from '@apollo/client';
import {
  mergeNewPage,
  pageInfoFromData,
  edgesFromData,
} from 'helpers/graphql-utils';
import { TAGS, START_DATE, END_DATE, USERS } from 'constants/filters';
import {
  COMPANY_ACTIVE_PROJECTS_QUERY,
  USER_RECENT_PROJECTS_QUERY,
  USER_FAVORITED_PROJECTS_QUERY,
  ARCHIVED_PROJECTS_QUERY,
} from './queries';
import getPathForFeedType from './getPathForFeedType';

function recentProjectsForFeedType(results, feedType) {
  const path = getPathForFeedType(feedType);

  return {
    pageInfo: pageInfoFromData(results, path),
    edges: edgesFromData(results, path),
  };
}

function hasNextPage(results, feedType) {
  return pageInfoFromData(results, getPathForFeedType(feedType)).hasNextPage;
}

function filtersToQueryArguments(filters, query) {
  const args = {};
  if (filters[TAGS]) {
    args.tags = filters[TAGS].map((tag) => tag.display_value);
  }
  if (filters[START_DATE]) {
    args.createdAfter = filters[START_DATE];
  }
  if (filters[END_DATE]) {
    args.createdBefore = filters[END_DATE];
  }
  if (filters[USERS]) {
    const userIds = [];
    const groupIds = [];
    filters[USERS].forEach((userGroupItem) => {
      if (userGroupItem.first_name) userIds.push(userGroupItem.node_id);
      else groupIds.push(userGroupItem.node_id);
    });
    args.users = userIds;
    args.groups = groupIds;
  }
  if (query && query.length) {
    args.query = query;
  }

  return args;
}

const feedTypesQueryMap = {
  ACTIVE: COMPANY_ACTIVE_PROJECTS_QUERY,
  MY_RECENT: USER_RECENT_PROJECTS_QUERY,
  USER_FAVORITED: USER_FAVORITED_PROJECTS_QUERY,
  ARCHIVED: ARCHIVED_PROJECTS_QUERY,
};

export default function useProjectFeed({ feedType, filters, query }) {
  const { loading, error, data, fetchMore, refetch, networkStatus } = useQuery(
    feedTypesQueryMap[feedType],
    {
      variables: filtersToQueryArguments(filters, query),
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
    },
  );

  const projects = useMemo(() => {
    if (networkStatus === NetworkStatus.refetch || error) {
      return [];
    }

    return recentProjectsForFeedType(data, feedType).edges;
  }, [feedType, data, networkStatus, error]);

  const fetchNextPage = async () => {
    const path = getPathForFeedType(feedType);
    const pageInfo = pageInfoFromData(data, path);
    return fetchMore({
      variables: {
        ...filtersToQueryArguments(filters, query),
        cursor: pageInfo.endCursor,
      },
      updateQuery: (oldData, result) => {
        return mergeNewPage(oldData, result, path);
      },
    });
  };

  return {
    data,
    loading,
    error,
    projects,
    fetchNextPage,
    refresh: refetch,
    refreshing: networkStatus === NetworkStatus.refetch,
    hasNextPage: hasNextPage(data, feedType),
    fetchingNextPage: networkStatus === NetworkStatus.fetchMore,
  };
}
